import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormHelperText, InputLabel, TextField } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { z } from "zod";
import useApi, { httpClient } from "../../hooks/useApi";
import Constants from "../../utils/Constants";
import MESSAGES from "../../utils/Messages";
import CorporateBondSpreadsheet from "../SpreadSheets/OwnedStock/CorporateBondSpreadsheet";
import EquitySpreadsheet from "../SpreadSheets/OwnedStock/EquitySpreadsheet";
import OtherStockSpreadsheet from "../SpreadSheets/OwnedStock/OtherStockSpreadsheet";
import OwnedStockSpreadsheet from "../SpreadSheets/OwnedStock/OwnedStockSpreadsheet";
import StockAcquisitionRightsSpreadsheet from "../SpreadSheets/OwnedStock/StockAcquisitionRightsSpreadsheet";

const OwnedStockConfirmation = forwardRef(
  (
    {
      targetData,
      presenceOrAbsenceList,
      postSaveHandler = () => {},
      readOnly = false,
    },
    ref
  ) => {
    const role = useSelector((state) => state.role.role);

    // 確認先のみの必須入力制御関数
    const requiredValidaterBuilderForConfirmer = (columnName) => {
      return (details) => {
        // 確認先でない場合は必須チェックしない
        if (role !== Constants.ROLE.CONFIRMER) return true;
        // 確認先の場合は必須チェックを行う
        if (
          details?.some(
            (row) =>
              // 「false」はOKのため「!」演算子は使用できない
              row[columnName] === "" ||
              row[columnName] === null ||
              row[columnName] === undefined
          )
        ) {
          return false;
        } else {
          return true;
        }
      };
    };
    // バリデーション定義
    const schema = z
      .object({
        id: z.string(),
        requestStatement: z
          .string()
          .max(
            Constants.MULTI_LINE_TEXT_MAX,
            MESSAGES.TEXT_MAX_ERROR(Constants.MULTI_LINE_TEXT_MAX)
          )
          .optional(),
        ownedStock: z
          .array(
            z
              .object({
                id: z.string(),
                details1: z
                  .string({
                    message: MESSAGES.REQUIRED_ERROR_WITH_NAME("株式の種類"),
                  })
                  .min(1, MESSAGES.REQUIRED_ERROR_WITH_NAME("株式の種類"))
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
                // differencesExistence: z.boolean(),
              })
              .passthrough()
          )
          .refine(
            requiredValidaterBuilderForConfirmer("differencesExistence"),
            {
              message: MESSAGES.REQUIRED_ERROR_WITH_NAME("相違の有無"),
              path: [0, "differencesExistence"],
            }
          )
          .refine(requiredValidaterBuilderForConfirmer("details3"), {
            message: MESSAGES.REQUIRED_ERROR_WITH_NAME("質権設定の有無"),
            path: [0, "details3"],
          })
          .refine(requiredValidaterBuilderForConfirmer("details6"), {
            message: MESSAGES.REQUIRED_ERROR_WITH_NAME("株券発行の有無"),
            path: [0, "details6"],
          })
          .optional(),
        equity: z
          .array(
            z
              .object({
                id: z.string(),
                details1: z
                  .string({
                    message: MESSAGES.REQUIRED_ERROR_WITH_NAME("出資の種類"),
                  })
                  .min(1, MESSAGES.REQUIRED_ERROR_WITH_NAME("出資の種類"))
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
              })
              .passthrough()
          )
          .refine(
            requiredValidaterBuilderForConfirmer("differencesExistence"),
            {
              message: MESSAGES.REQUIRED_ERROR_WITH_NAME("相違の有無"),
              path: [0, "differencesExistence"],
            }
          )
          .refine(requiredValidaterBuilderForConfirmer("details3"), {
            message: MESSAGES.REQUIRED_ERROR_WITH_NAME("質権設定の有無"),
            path: [0, "details3"],
          })
          .optional(),
        corporateBond: z
          .array(
            z
              .object({
                id: z.string(),
                details1: z
                  .string({
                    message:
                      MESSAGES.REQUIRED_ERROR_WITH_NAME("社債の種類及び名称"),
                  })
                  .min(
                    1,
                    MESSAGES.REQUIRED_ERROR_WITH_NAME("社債の種類及び名称")
                  )
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
              })
              .passthrough()
          )
          .refine(
            requiredValidaterBuilderForConfirmer("differencesExistence"),
            {
              message: MESSAGES.REQUIRED_ERROR_WITH_NAME("相違の有無"),
              path: [0, "differencesExistence"],
            }
          )
          .refine(requiredValidaterBuilderForConfirmer("details3"), {
            message: MESSAGES.REQUIRED_ERROR_WITH_NAME("質権設定の有無"),
            path: [0, "details3"],
          })
          .optional(),
        stockAcquisittonRights: z
          .array(
            z
              .object({
                id: z.string(),
                details1: z
                  .string({
                    message: MESSAGES.REQUIRED_ERROR_WITH_NAME("種類及び名称"),
                  })
                  .min(1, MESSAGES.REQUIRED_ERROR_WITH_NAME("種類及び名称"))
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
              })
              .passthrough()
          )
          .refine(
            requiredValidaterBuilderForConfirmer("differencesExistence"),
            {
              message: MESSAGES.REQUIRED_ERROR_WITH_NAME("相違の有無"),
              path: [0, "differencesExistence"],
            }
          )
          .refine(requiredValidaterBuilderForConfirmer("details3"), {
            message: MESSAGES.REQUIRED_ERROR_WITH_NAME("質権設定の有無"),
            path: [0, "details3"],
          })
          .optional(),
        otherStock: z
          .array(
            z
              .object({
                id: z.string(),
                details1: z
                  .string({
                    message: MESSAGES.REQUIRED_ERROR_WITH_NAME("種類"),
                  })
                  .min(1, MESSAGES.REQUIRED_ERROR_WITH_NAME("種類"))
                  .max(
                    Constants.ONE_LINE_TEXT_MAX,
                    MESSAGES.TEXT_MAX_ERROR(Constants.ONE_LINE_TEXT_MAX)
                  ),
              })
              .passthrough()
          )
          .refine(
            requiredValidaterBuilderForConfirmer("differencesExistence"),
            {
              message: MESSAGES.REQUIRED_ERROR_WITH_NAME("相違の有無"),
              path: [0, "differencesExistence"],
            }
          )
          .refine(requiredValidaterBuilderForConfirmer("details3"), {
            message: MESSAGES.REQUIRED_ERROR_WITH_NAME("質権設定の有無"),
            path: [0, "details3"],
          })
          .optional(),
        differences: z
          .string()
          .max(
            Constants.MULTI_LINE_TEXT_MAX,
            MESSAGES.TEXT_MAX_ERROR(Constants.MULTI_LINE_TEXT_MAX)
          )
          .optional(),
      })
      .passthrough()
      .refine(
        // 「相違の有無」が「有り」のデータがある場合、相違内容は必須
        (data) => {
          // 確認先でない場合は必須チェックしない
          if (role !== Constants.ROLE.CONFIRMER) return true;
          // 確認先の場合は必須チェックを行う
          if (
            data?.ownedStock?.some((row) => row.differencesExistence) ||
            data?.equity?.some((row) => row.differencesExistence) ||
            data?.corporateBond?.some((row) => row.differencesExistence) ||
            data?.stockAcquisittonRights?.some(
              (row) => row.differencesExistence
            ) ||
            data?.otherStock?.some((row) => row.differencesExistence)
          ) {
            return !!data?.differences;
          } else {
            return true;
          }
        },
        {
          message:
            "「相違の有無」が「有り」のデータがある場合、" +
            MESSAGES.REQUIRED_ERROR_WITH_NAME("相違内容"),
          path: ["differences"],
        }
      );

    const {
      control,
      handleSubmit,
      setValue,
      getValues,
      reset,
      trigger,
      formState: { errors, isDirty, isSubmitting },
    } = useForm({
      mode: "onSubmit",
      resolver: zodResolver(schema),
    });
    const { connect } = useApi();
    const params = useParams();

    const [displayOwnedStock, setDisplayOwnedStock] = useState(false);
    const [displayEquity, setDisplayEquity] = useState(false);
    const [displayCorporateBond, setDisplayCorporateBond] = useState(false);
    const [displayStockAcquisittonRights, setDisplayStockAcquisittonRights] =
      useState(false);
    const [displayOtherStock, setDisplayOtherStock] = useState(false);

    // 初期化
    useEffect(() => {
      // フォームの初期値設定
      reset({
        ...targetData,
        ownedStock: targetData?.confirmationDetails?.filter(
          (row) => row.kubun === Constants.OWNED_STOCK_KUBUN.OWNED_STOCK
        ),
        equity: targetData?.confirmationDetails?.filter(
          (row) => row.kubun === Constants.OWNED_STOCK_KUBUN.EQUITY
        ),
        corporateBond: targetData?.confirmationDetails?.filter(
          (row) => row.kubun === Constants.OWNED_STOCK_KUBUN.CORPORATE_BOND
        ),
        stockAcquisittonRights: targetData?.confirmationDetails?.filter(
          (row) =>
            row.kubun === Constants.OWNED_STOCK_KUBUN.STOCK_ACQUISITION_RIGHTS
        ),
        otherStock: targetData?.confirmationDetails?.filter(
          (row) => row.kubun === Constants.OWNED_STOCK_KUBUN.OTHER_STOCK
        ),
      });

      // フォーム項目の表示制御
      // （確認先またはreadonly）かつ「株式」の確認内容がない場合は非表示
      // =（確認先またはreadonly）でない または 「株式」の確認内容がある場合表示
      setDisplayOwnedStock(
        !(role === Constants.ROLE.CONFIRMER || readOnly) ||
          getValues("ownedStock")?.length > 0
      );
      setDisplayEquity(
        !(role === Constants.ROLE.CONFIRMER || readOnly) ||
          getValues("equity")?.length > 0
      );
      setDisplayCorporateBond(
        !(role === Constants.ROLE.CONFIRMER || readOnly) ||
          getValues("corporateBond")?.length > 0
      );
      setDisplayStockAcquisittonRights(
        !(role === Constants.ROLE.CONFIRMER || readOnly) ||
          getValues("stockAcquisittonRights")?.length > 0
      );
      setDisplayOtherStock(
        !(role === Constants.ROLE.CONFIRMER || readOnly) ||
          getValues("otherStock")?.length > 0
      );
    }, [getValues, readOnly, reset, role, targetData]);

    // API POST
    const onSubmit = async (data) => {
      // 確認詳細データの整形
      formatDataBeforeSubmit(data);
      const result = await connect(() => {
        return httpClient.post(`/api/${role}/UpsertConfirmation`, data);
      }, "保存しました");
      if (result?.status === 200) {
        postSaveHandler();
      }
      console.log("post end", data);
    };
    // 送信前のデータ整形
    const formatDataBeforeSubmit = (data) => {
      data.confirmationDetails = [];
      if (data.ownedStock?.length) {
        data.confirmationDetails.push(...data.ownedStock);
      }
      if (data.equity?.length) {
        data.confirmationDetails.push(...data.equity);
      }
      if (data.corporateBond?.length) {
        data.confirmationDetails.push(...data.corporateBond);
      }
      if (data.stockAcquisittonRights?.length) {
        data.confirmationDetails.push(...data.stockAcquisittonRights);
      }
      if (data.otherStock?.length) {
        data.confirmationDetails.push(...data.otherStock);
      }
      return data;
    };

    // 親コンポーネントへの関数移譲
    useImperativeHandle(ref, () => ({
      // 親コンポーネントからフォームの値を参照可能にする
      getFormData: () => {
        return formatDataBeforeSubmit(getValues());
      },
      // 親コンポーネントからバリデーション発火を可能にする
      validate: async () => {
        const isValid = await trigger();
        return isValid;
      },
      // 親コンポーネントからisDirtyを取得する
      getIsDirty: () => isDirty,
      // 親コンポーネントからisDirtyをリセットする
      resetIsDirty: () => {
        reset(getValues(), {
          keepDirty: false,
          keepTouched: false,
        });
      },
    }));

    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          {role !== Constants.ROLE.CONFIRMER && (
            <Controller
              name="requestStatement"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  label="確認依頼文"
                  {...field}
                  multiline
                  fullWidth
                  disabled={role === Constants.ROLE.CLIENT || readOnly}
                  minRows={2}
                  sx={{ marginBottom: "1rem" }}
                />
              )}
            />
          )}
          {displayOwnedStock && (
            <>
              <InputLabel>■株式</InputLabel>
              <OwnedStockSpreadsheet
                disableConfirmation={
                  targetData?.confirmationLetterStatus !==
                    Constants.CONFIRMATION_LETTER_STATUS.REGISTERED || readOnly
                }
                disableResponse={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  (role === Constants.ROLE.CONFIRMER &&
                    params.type !== "create" &&
                    targetData?.confirmerAuthority ===
                      Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL) ||
                  readOnly
                }
                confirmationLetterId={targetData?.id}
                targetData={targetData?.ownedStock}
                presenceOrAbsenceList={presenceOrAbsenceList}
                control={control}
                setValue={setValue}
              />
              <FormHelperText className="Mui-error">
                {errors?.ownedStock?.map((row) => {
                  let errorFields = Object.keys(row);
                  return (
                    <>
                      {errorFields?.map((key) => (
                        <>
                          {row[key]?.message}
                          <br />
                        </>
                      ))}
                    </>
                  );
                })}
              </FormHelperText>
              <br />
            </>
          )}
          {displayEquity && (
            // 確認先かつ「持分」の確認内容がない場合は非表示
            <>
              <InputLabel>■持分</InputLabel>
              <EquitySpreadsheet
                disableConfirmation={
                  targetData?.confirmationLetterStatus !==
                    Constants.CONFIRMATION_LETTER_STATUS.REGISTERED || readOnly
                }
                disableResponse={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  (role === Constants.ROLE.CONFIRMER &&
                    params.type !== "create" &&
                    targetData?.confirmerAuthority ===
                      Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL) ||
                  readOnly
                }
                confirmationLetterId={targetData?.id}
                targetData={targetData?.ownedStock}
                presenceOrAbsenceList={presenceOrAbsenceList}
                control={control}
                setValue={setValue}
              />
              <FormHelperText className="Mui-error">
                {errors?.equity?.map((row) => {
                  let errorFields = Object.keys(row);
                  return (
                    <>
                      {errorFields?.map((key) => (
                        <>
                          {row[key]?.message}
                          <br />
                        </>
                      ))}
                    </>
                  );
                })}
              </FormHelperText>
              <br />
            </>
          )}
          {displayCorporateBond && (
            // 確認先かつ「社債」の確認内容がない場合は非表示
            <>
              <InputLabel>■社債</InputLabel>
              <CorporateBondSpreadsheet
                disableConfirmation={
                  targetData?.confirmationLetterStatus !==
                    Constants.CONFIRMATION_LETTER_STATUS.REGISTERED || readOnly
                }
                disableResponse={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  (role === Constants.ROLE.CONFIRMER &&
                    params.type !== "create" &&
                    targetData?.confirmerAuthority ===
                      Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL) ||
                  readOnly
                }
                confirmationLetterId={targetData?.id}
                targetData={targetData?.ownedStock}
                presenceOrAbsenceList={presenceOrAbsenceList}
                control={control}
                setValue={setValue}
              />
              <FormHelperText className="Mui-error">
                {errors?.corporateBond?.map((row) => {
                  let errorFields = Object.keys(row);
                  return (
                    <>
                      {errorFields?.map((key) => (
                        <>
                          {row[key]?.message}
                          <br />
                        </>
                      ))}
                    </>
                  );
                })}
              </FormHelperText>
              <br />
            </>
          )}

          {displayStockAcquisittonRights && (
            // 確認先かつ「新株予約権等」の確認内容がない場合は非表示
            <>
              <InputLabel>■新株予約権等</InputLabel>
              <StockAcquisitionRightsSpreadsheet
                disableConfirmation={
                  targetData?.confirmationLetterStatus !==
                    Constants.CONFIRMATION_LETTER_STATUS.REGISTERED || readOnly
                }
                disableResponse={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  (role === Constants.ROLE.CONFIRMER &&
                    params.type !== "create" &&
                    targetData?.confirmerAuthority ===
                      Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL) ||
                  readOnly
                }
                confirmationLetterId={targetData?.id}
                // targetData={targetData?.ownedStock}
                presenceOrAbsenceList={presenceOrAbsenceList}
                control={control}
                setValue={setValue}
              />
              <FormHelperText className="Mui-error">
                {errors?.stockAcquisittonRights?.map((row) => {
                  let errorFields = Object.keys(row);
                  return (
                    <>
                      {errorFields?.map((key) => (
                        <>
                          {row[key]?.message}
                          <br />
                        </>
                      ))}
                    </>
                  );
                })}
              </FormHelperText>
              <br />
            </>
          )}

          {displayOtherStock && (
            // 確認先かつ「その他」の確認内容がない場合は非表示
            <>
              <InputLabel>■その他</InputLabel>
              <OtherStockSpreadsheet
                disableConfirmation={
                  targetData?.confirmationLetterStatus !==
                    Constants.CONFIRMATION_LETTER_STATUS.REGISTERED || readOnly
                }
                disableResponse={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  (role === Constants.ROLE.CONFIRMER &&
                    params.type !== "create" &&
                    targetData?.confirmerAuthority ===
                      Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL) ||
                  readOnly
                }
                confirmationLetterId={targetData?.id}
                targetData={targetData?.ownedStock}
                presenceOrAbsenceList={presenceOrAbsenceList}
                control={control}
                setValue={setValue}
              />
              <FormHelperText className="Mui-error">
                {errors?.otherStock?.map((row) => {
                  let errorFields = Object.keys(row);
                  return (
                    <>
                      {errorFields?.map((key) => (
                        <>
                          {row[key]?.message}
                          <br />
                        </>
                      ))}
                    </>
                  );
                })}
              </FormHelperText>
              <br />
            </>
          )}

          <Controller
            name="differences"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="相違内容"
                {...field}
                multiline
                fullWidth
                disabled={
                  role === Constants.ROLE.CLIENT ||
                  (role === Constants.ROLE.AUDITOR &&
                    targetData?.sendingMethod ===
                      Constants.SENDING_METHOD.EMAIL_OR_POST) ||
                  (role === Constants.ROLE.CONFIRMER &&
                    params.type !== "create" &&
                    targetData?.confirmerAuthority ===
                      Constants.CONFIRMER_AUTHORITY_TYPE.APPROVAL) ||
                  readOnly
                }
                minRows={2}
                error={!!errors.differences}
                helperText={
                  errors.differences ? errors.differences.message : ""
                }
              />
            )}
          />
          {!readOnly && role !== Constants.ROLE.CONFIRMER && (
            <Button type="submit" disabled={isSubmitting}>
              保存
            </Button>
          )}
        </form>
      </>
    );
  }
);

export default OwnedStockConfirmation;
